import { Observable } from 'rxjs';
import { Store } from 'redux';
import { ActionsObservable } from 'redux-observable';
import { startSubmit, stopSubmit, initialize } from 'redux-form/immutable';

import OrganizationApi from 'entities/organization/api';
import { selectOrganization } from 'entities/user/selectors';
import { StoreUserAction } from 'entities/user/actions';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import { STORE as STORE_USER } from 'entities/user/constants';

import {
  NOTIFICATION_BANNER_FORM_IDENTIFIER,
  HOMEPAGE_BANNER_FORM_IDENTIFIER,
  INIT,
  SAVE_NOTIFICATION_BANNER,
  SAVE_FORM,
  DISCLAIMERS_FORM_IDENTIFIER,
  SAVE_DISCLAIMERS,
  SPLASH_IMAGE_FORM_IDENTIFIER,
} from './constants';
import {
  InitAction,
  SaveNotificationBannerAction,
  SaveDisclaimersAction,
  SaveFormAction,
  initFailedAction,
} from './actions';

const init = (action$: ActionsObservable<InitAction | StoreUserAction>, store: Store) =>
  action$.ofType(INIT, STORE_USER).mergeMap(() => {
    const state = store.getState();
    const organization = selectOrganization(state);

    if (!organization) {
      return [];
    }

    return Observable.of(
      initialize(NOTIFICATION_BANNER_FORM_IDENTIFIER, organization.notificationBanner),
      initialize(HOMEPAGE_BANNER_FORM_IDENTIFIER, {
        homepageBanner: organization.homepageBanner,
        homepageTagline: organization.homepageTagline,
        hideHomepageTagline: organization.hideHomepageTagline,
        homepageGradient: organization.homepageGradient,
      }),
      initialize(SPLASH_IMAGE_FORM_IDENTIFIER, {
        splashBackgroundImage: organization.splashBackgroundImage,
      }),
      initialize(DISCLAIMERS_FORM_IDENTIFIER, { disclaimers: organization.disclaimers })
    ).catch((e) => Observable.of(initFailedAction(e)));
  });

const submitNotificationBanner = (
  action$: ActionsObservable<SaveNotificationBannerAction>,
  store: Store
) =>
  action$.ofType(SAVE_NOTIFICATION_BANNER).mergeMap((payload) =>
    Observable.concat(
      Observable.of(startSubmit(NOTIFICATION_BANNER_FORM_IDENTIFIER)).switchMap(() => {
        const { data } = payload;
        const organization = selectOrganization(store.getState());

        return OrganizationApi.update(organization.id, {
          notificationBanner: data,
        })
          .mergeMap((action) => {
            const populatedOrganization = selectOrganization(store.getState());
            // Override populated values to prevent overriding with raw values
            const updatedOrganization = action.organization.set(
              'locations',
              populatedOrganization.get('locations')
            );
            return [
              { ...action, organization: updatedOrganization },
              stopSubmit(NOTIFICATION_BANNER_FORM_IDENTIFIER),
              initialize(
                NOTIFICATION_BANNER_FORM_IDENTIFIER,
                action.organization.get('notificationBanner')
              ),
              showMessageAction('Changes Saved!'),
            ];
          })
          .catch(() =>
            Observable.of(
              stopSubmit(NOTIFICATION_BANNER_FORM_IDENTIFIER),
              showErrorAction('An error occurred during saving, please try again.')
            )
          );
      })
    )
  );

const submitDisclaimers = (action$: ActionsObservable<SaveDisclaimersAction>, store: Store) =>
  action$.ofType(SAVE_DISCLAIMERS).mergeMap((payload) =>
    Observable.concat(
      Observable.of(startSubmit(DISCLAIMERS_FORM_IDENTIFIER)).switchMap(() => {
        const organization = selectOrganization(store.getState());

        const disclaimers = payload.data.disclaimers.map(({ disclaimerExpire, ...disclaimer }) => ({
          ...disclaimer,
          _id: disclaimer._id || undefined,
          buttonText: disclaimer.buttonText || null,
          disclaimerExpire: disclaimerExpire || 0,
        }));

        return OrganizationApi.update(organization!.id, { disclaimers })
          .mergeMap((action) => [
            stopSubmit(DISCLAIMERS_FORM_IDENTIFIER),
            initialize(DISCLAIMERS_FORM_IDENTIFIER, {
              disclaimers: action.organization.get('disclaimers'),
            }),
            showMessageAction('Changes Saved!'),
          ])
          .catch(() =>
            Observable.of(
              stopSubmit(DISCLAIMERS_FORM_IDENTIFIER),
              showErrorAction('An error occurred during saving, please try again.')
            )
          );
      })
    )
  );

const submitFormData = (action$: ActionsObservable<SaveFormAction>, store: Store) =>
  action$.ofType(SAVE_FORM).mergeMap(({ data }) => {
    const organization = selectOrganization(store.getState());

    return Observable.of(startSubmit(HOMEPAGE_BANNER_FORM_IDENTIFIER)).switchMap(() =>
      OrganizationApi.update(organization.id, {
        ...data,
      })
        .mergeMap((action) => [
          action,
          showMessageAction('Changes Saved!'),
          initialize(HOMEPAGE_BANNER_FORM_IDENTIFIER, {
            homepageBanner: action.organization.homepageBanner,
            homepageTagline: action.organization.homepageTagline,
            hideHomepageTagline: action.organization.hideHomepageTagline,
            homepageGradient: action.organization.homepageGradient,
          }),
          stopSubmit(HOMEPAGE_BANNER_FORM_IDENTIFIER),
        ])
        .catch(() =>
          Observable.of(
            showErrorAction('An error occurred during saving, please try again.'),
            stopSubmit(HOMEPAGE_BANNER_FORM_IDENTIFIER)
          )
        )
    );
  });

export default [init, submitNotificationBanner, submitFormData, submitDisclaimers];
